import { ReactNode, useEffect } from "react";
import { Helmet } from "react-helmet";
import { CustomerPortalHeader } from "./CustomerPortalHeader";
import { useOrganizationBranding } from "@/hooks/useOrganizationBranding";
import { useCustomerAuth } from "./CustomerAuthProvider";
import { PortalInstallPrompt } from "@/components/portal/PortalInstallPrompt";
import { PWADebugPanel } from "@/components/portal/PWADebugPanel";

import { LanguageProvider } from "@/contexts/LanguageContext";
import { DEFAULT_LANGUAGE, isLanguageCode } from "@/localization/languages";
import type { Language } from "@/localization/types";

interface CustomerPortalLayoutProps {
  children: ReactNode;
}

export function CustomerPortalLayout({ children }: CustomerPortalLayoutProps) {
  const { name, logoUrl, faviconUrl, brandColorHex, defaultPortalLanguage, isLoading } = useOrganizationBranding();
  const { customer } = useCustomerAuth();
  const organizationId = customer?.organization_id;

  useEffect(() => {
    // Set the theme color meta tag dynamically
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', brandColorHex);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = brandColorHex;
      document.head.appendChild(meta);
    }

    // Set apple touch icon if available
    if (logoUrl) {
      const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (appleTouchIcon) {
        appleTouchIcon.setAttribute('href', logoUrl);
      } else {
        const link = document.createElement('link');
        link.rel = 'apple-touch-icon';
        link.href = logoUrl;
        document.head.appendChild(link);
      }
    }
  }, [brandColorHex, logoUrl]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-elegant">
        <div className="text-center space-y-4">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading your bridal portal...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* PWA Manifest is handled by usePortalPWA hook to avoid conflicts */}
        
        {/* Theme and branding */}
        <meta name="theme-color" content={brandColorHex} />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={`${name} Portal`} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="application-name" content={`${name} Portal`} />
        
        {/* iOS specific meta tags */}
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        
        {/* Favicon */}
        {faviconUrl ? (
          <link rel="icon" href={faviconUrl} type="image/png" />
        ) : logoUrl ? (
          <link rel="icon" href={logoUrl} type="image/png" />
        ) : null}
        
        {/* Apple Touch Icon - Use portal icons service */}
        <link 
          rel="apple-touch-icon" 
          href={`/functions/v1/portal-icons/icon-192.png${organizationId ? `?org=${organizationId}` : ''}`}
        />
        <link 
          rel="apple-touch-icon" 
          sizes="180x180" 
          href={`/functions/v1/portal-icons/icon-192.png${organizationId ? `?org=${organizationId}` : ''}`}
        />
        
        {/* Page title */}
        <title>{name} — My Bridal Journey</title>
      </Helmet>
      
      <LanguageProvider organizationDefault={(isLanguageCode(defaultPortalLanguage || '') ? defaultPortalLanguage : DEFAULT_LANGUAGE) as Language}>
        <div className="min-h-screen bg-background">
          <CustomerPortalHeader />
          
          {/* Install prompt */}
          <PortalInstallPrompt variant="banner" />
          
          <main className="container mx-auto px-3 py-4 sm:px-4 sm:py-6 lg:py-8 max-w-7xl">
            {children}
          </main>
          
          {/* PWA Debug Panel - always visible for debugging */}
          <PWADebugPanel />
        </div>
      </LanguageProvider>
    </>
  );
}
